import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaTruck, FaNewspaper, FaImage } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import TimelineImage from './TimelineImage';
import ImageViewer from '../common/ImageViewer';
import { useImageViewer } from '../../hooks/useImageViewer';

export default function TimelineContent({ event, onNext, onPrev }) {
  const { isOpen, currentImage, openImage, closeImage } = useImageViewer();

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="grid md:grid-cols-2 gap-8 items-center"
      >
        {/* Image Side */}
        <AnimatePresence mode="wait">
          <TimelineImage 
            key={event.year} 
            year={event.year} 
            onImageClick={openImage}
          />
        </AnimatePresence>

        {/* Content Side */}
        <AnimatePresence mode="wait">
          <motion.div
            key={event.year}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="inline-block px-4 py-2 bg-red-600 text-white rounded-full font-bold">
              {event.year}
            </div>
            
            <h3 className="text-3xl font-bold text-gray-900 retro-text">
              {event.title}
            </h3>
            
            <p className="text-gray-600 leading-relaxed">
              {event.description}
            </p>

            {/* Special buttons for specific years */}
            {event.year === 2017 && (
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a
                  href="https://ekstrabladet.dk/nyheder/samfund/ikonisk-poelsevogn-braendt-ned-vi-kommer-tilbage/6844877"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-full font-medium
                           shadow-xl shadow-red-600/20 hover:bg-red-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaNewspaper className="mr-2 w-5 h-5" />
                  <span>Læs Artiklen</span>
                </motion.a>

                <motion.button
                  onClick={() =>
                    openImage('https://i.postimg.cc/xXt0w6vR/image-6-webp.webp', "Benny's Pølsevogn 2017")
                  }
                  className="inline-flex items-center px-6 py-3 bg-white text-red-600 rounded-full font-medium
                             border-2 border-red-600/30 shadow-xl shadow-red-600/10 
                             hover:bg-red-50 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaImage className="mr-2 w-5 h-5" />
                  <span>Se Illustration</span>
                </motion.button>
              </div>
            )}

            {event.year === 2025 && (
              <Link to="/rental">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-full font-medium
                           shadow-xl shadow-red-600/20 hover:bg-red-700 transition-colors"
                >
                  <FaTruck className="mr-2 w-5 h-5" />
                  <span>Lej Vores Foodtruck</span>
                </motion.div>
              </Link>
            )}

            <div className="flex items-center gap-4 pt-4">
              <button
                onClick={onPrev}
                className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
              >
                <FaChevronLeft className="w-5 h-5" />
              </button>

              <button
                onClick={onNext}
                className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
              >
                <FaChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Image Viewer */}
      <ImageViewer
        isOpen={isOpen}
        onClose={closeImage}
        imageUrl={currentImage.url}
        alt={currentImage.alt}
      />
    </>
  );
}
